/*
 * The root is the entrypoint of the clientside javascript bundle,
 * it's job is to render the components of the SPA to the root DOM node
 */

// Sentry must be imported before everything else
import './services/sentry';

import dayjs from "dayjs";
import "dayjs/locale/sv";
import * as React from "react";
import { Suspense } from "react";
import { createRoot } from "react-dom/client";
import { Route, Routes } from "react-router-dom";
import { appNode } from "../shared-exports";
import { ErrorBoundary } from "./ErrorBoundary";
import {
  EditorProvider,
  ModalProvider,
  ProjectModalProvider,
  EditorModalProvider,
  CartModalProvider,
  MeasurementModalProvider,
  ProductInfoModalProvider,
  ColorMaterialModalProvider,
  ProviderRouter as Router,
  UserProvider,
  DataProvider,
  DataContext,
  OverlayProvider
} from "../providers";
import { ScrollCache } from "../providers/scrollCache";
import { ModelStorage } from "../services";
import { Spin } from "./ui";
import { I18nextProvider } from "react-i18next";
import i18n from "./services/i18";
import { useTranslation } from 'react-i18next';
import '../style/globals.css';
import '../style/custom.css';
import '../style/container.css';
import { publicRoutes } from "./route-config";
import { AuthGuard } from "./auth/AuthGuard";

const styles = {
  loader: {
    width: '100%',
    height: '80vh',
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
};

dayjs.locale("sv");

// Setup service worker
(function () {
  if (!__DEV__ && "serviceWorker" in navigator) {
    window.addEventListener("load", async () => {
      try {

      } catch (err) {
        console.error("SW Registration failed:", err);
      }
    });
  }
})();

function ViewRouter() {
  ModelStorage.verifyClientProperties();

  const data = React.useContext(DataContext);
  const { t, i18n } = useTranslation();
  data.translate = t;
  data.i18n = i18n;

  return (
    <Suspense
      fallback={
        <div style={styles.loader}>
          <Spin size="large" />
        </div>
      }
    >
      <Routes>
        {publicRoutes.map(({ path, component: Component, props }) => (
          <Route key={path} path={path} element={<Component path={path} {...props} />} />
        ))}
        <Route path="/*" element={<AuthGuard />} />
      </Routes>
    </Suspense>
  );
}

// Render the application
const root = createRoot(document.getElementById(appNode)!);
root.render(
  <ErrorBoundary>
    <Router>
      <I18nextProvider i18n={i18n}>
        <DataProvider>
          <UserProvider>
            <ModalProvider>
              <ProjectModalProvider>
                <ColorMaterialModalProvider>
                  <EditorModalProvider>
                    <CartModalProvider>
                      <MeasurementModalProvider>
                        <ProductInfoModalProvider>
                          <EditorProvider>
                            <ScrollCache>
                              <OverlayProvider>
                                <ViewRouter />
                              </OverlayProvider>
                            </ScrollCache>
                          </EditorProvider>
                        </ProductInfoModalProvider>
                      </MeasurementModalProvider>
                    </CartModalProvider>
                  </EditorModalProvider>
                </ColorMaterialModalProvider>
              </ProjectModalProvider>
            </ModalProvider>
          </UserProvider>
        </DataProvider>
      </I18nextProvider>
    </Router>
  </ErrorBoundary>
);
